import { useDisclosure } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { FieldPathByValue, FieldValues, useFormContext } from 'react-hook-form';

import { trpc } from '../config/trpc';
import { useTargetAccount } from '../hooks/useTargetAccount';
import { ContactModal } from './ContactModal';
import { ContactFormValues } from './Forms/ContactForm';
import { SimpleSelect, SimpleSelectProps } from './SimpleSelect';

type Props<FormSchema extends FieldValues = FieldValues> = Omit<SimpleSelectProps<FormSchema>, 'options' | 'name'> & {
  name: FieldPathByValue<FormSchema, number>;
};

export const SimpleContactSelect = <FormSchema extends FieldValues = FieldValues>(props: Props<FormSchema>) => {
  const { setValue } = useFormContext<FormSchema>();
  const { targetAccount } = useTargetAccount();
  const { mutateAsync: createContact } = trpc.createContact.useMutation();
  const { data: contacts, refetch } = trpc.getContactsByAccountId.useQuery({
    id: targetAccount.id,
  });

  const options = useMemo(() => {
    const contactOptions =
      contacts?.map((contact) => ({
        label: contact.full_name,
        value: contact.id,
      })) || [];
    return [
      ...contactOptions,
      {
        label: '+ New Contact',
        value: -1,
      },
    ];
  }, [contacts]);

  const modalDisclosure = useDisclosure();

  const onContactCreate = useCallback(
    async (e: ContactFormValues) => {
      const newContact = await createContact(e);
      await refetch();
      setValue(props?.name, newContact.id as any);
    },
    [createContact, props?.name, refetch, setValue],
  );
  const onSelectChange = useCallback(
    (id: number) => {
      if (id === -1) {
        modalDisclosure.onOpen();
        setValue(props?.name, undefined);
      }
    },
    [modalDisclosure, props.name, setValue],
  );

  return (
    <>
      <SimpleSelect<FormSchema> {...props} onChangeCallback={onSelectChange} options={options} />
      <ContactModal
        {...modalDisclosure}
        defaultValues={{}}
        type="Create"
        onSubmit={onContactCreate}
        onClose={modalDisclosure.onClose}
      />
    </>
  );
};
